import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { ResponseByPath, SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'
import featureFlagsDev from '@@/feature-flags-dev.json'
import featureFlagsLocal from '@@/feature-flags-dev.local.json'

const featureFlags = { ...featureFlagsDev, ...featureFlagsLocal }

export type SidebarSectionData = SchemaAttributes<'SidebarSection'>

export const model = Model as ModelDefinition<SidebarSectionData>

export const factory = Factory.extend<SidebarSectionData>({
  is_legacy_url() {
    return false
  },
  label(n) {
    return `Section ${n}`
  },
  target_url: null,
  links() {
    return []
  },
})

type Response = ResponseByPath<'get', '/{provet_id}/api/navigation/sidebar'>
type NavItem = { is_legacy_url: boolean; target_url: string | null }

// remaps URLs to either point to legacy or new
// depending on the is_legacy_url field
function mapTargetUrl<T extends NavItem>(item: T, provetId: string): T {
  const config = useRuntimeConfig()

  if (!item.target_url) {
    return item
  }

  // if a legacy url, use legacyBaseUrl (points to review app when configured)
  // else use the current origin for frontend pages
  const baseUrl = item.is_legacy_url
    ? config.public.legacyBaseUrl
    : `${location.origin}${config.app.baseURL}`

  // Ensure baseUrl ends with a slash for proper URL construction
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`

  const effectiveProvetId =
    item.is_legacy_url && config.public.remoteProvetId
      ? config.public.remoteProvetId
      : provetId

  return {
    ...item,
    target_url: `${normalizedBaseUrl}${effectiveProvetId}${item.target_url}`,
  }
}

export function routes(server: MockServer) {
  server.get(
    '/navigation/sidebar',
    function (this: MirageServer<Response>, schema, request) {
      const response = this.serialize(schema.all('sidebarSection'))
      const provetId = request.params.provet_id

      // we need to map target urls immutably, so we don't alter the original data held by mirage
      response.data = response.data.map((section) => {
        const attributes = mapTargetUrl(section.attributes!, provetId)
        attributes.links = attributes.links.map((link) =>
          mapTargetUrl(link, provetId),
        )

        return {
          ...section,
          attributes,
        }
      })

      return response
    },
  )
}

export const fixtures = [
  {
    id: 'dashboard',
    label: 'Patient care',
    links: [
      {
        label: 'Dashboard',
        target_url: '/dashboard',
        is_legacy_url: true,
      },
      {
        label: 'My workspace',
        target_url: '/workspace',
        is_legacy_url: true,
      },
      {
        label: 'Plans',
        target_url: '/treatment_plan/dashboard/',
        is_legacy_url: true,
      },
      {
        label: 'Laboratory',
        target_url: '/laboratory/referrals/',
        is_legacy_url: true,
      },
      {
        label: 'Patient referrals',
        target_url: '/diagnostic_imaging/referrals/',
        is_legacy_url: true,
      },
      {
        label: 'Patient locations',
        target_url: '/patient-care/patient-locations/',
        is_legacy_url: false,
      },
    ],
  },
  {
    id: 'clients_patients',
    label: 'Records',
    links: [
      {
        id: 'clients_patients_link',
        label: 'Clients & Patients',
        target_url: '/search/',
        is_legacy_url: true,
      },
      ...(featureFlags['temporary.conversation']
        ? [
            {
              id: 'conversations',
              label: 'Conversations',
              target_url: '/conversations/',
              is_legacy_url: false,
            },
          ]
        : featureFlags['temporary.conversations.early_access']
          ? [
              {
                id: 'conversations',
                label: 'Conversations',
                target_url: '/conversations/early-access',
                is_legacy_url: false,
              },
            ]
          : []),
      {
        label: 'Estimates',
        target_url: '/estimate/',
        is_legacy_url: true,
      },
      {
        label: 'Invoices',
        target_url: '/billing/invoice/',
        is_legacy_url: true,
      },
      {
        label: 'Insurance claims',
        target_url: '/insurance/claims/v2/',
        is_legacy_url: true,
      },
      {
        label: 'Reminders',
        target_url: '/reminder/list/',
        is_legacy_url: true,
      },
      {
        label: 'Written Prescriptions',
        target_url: '/general_presc/list/',
        is_legacy_url: true,
      },
      ...(featureFlags['temporary.new_frontend.health_plans']
        ? [
            {
              label: 'Health plan',
              target_url: '/health-plans/overview/',
              is_legacy_url: false,
            },
          ]
        : [
            {
              label: 'Health plan',
              target_url: '/health_plans/list/',
              is_legacy_url: true,
            },
          ]),
      ...(featureFlags['temporary.new_frontend.digital_practice_chat_widget']
        ? [
            {
              label: 'Daily chat list',
              target_url: '/records/daily/',
              is_legacy_url: false,
            },
          ]
        : []),
    ],
  },
  {
    id: 'calendar',
    label: 'Calendar',
    links: [
      ...(featureFlags['temporary.new_frontend.new_calendar']
        ? [
            {
              label: 'Appointments',
              target_url: '/calendar/appointments',
              is_legacy_url: false,
            },
          ]
        : [
            {
              label: 'Appointments',
              target_url: '/personnel/appointments/',
              is_legacy_url: true,
            },
          ]),
      {
        label: 'Boarding',
        target_url: '/calendar/boarding/',
        is_legacy_url: false,
      },
      {
        label: 'Finder',
        target_url: '/personnel/appointment_finder/',
        is_legacy_url: true,
      },
      {
        label: 'Shifts',
        target_url: '/personnel/shifts/',
        is_legacy_url: true,
      },
      {
        label: 'Day plan',
        target_url: '/personnel/dayplan/',
        is_legacy_url: true,
      },
      {
        label: 'Booking capacity',
        target_url: '/personnel/booking_capacity/',
        is_legacy_url: true,
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    links: [
      {
        label: 'Items',
        target_url: '/catalog/items/',
        is_legacy_url: false,
      },
      {
        label: 'Bundles',
        target_url: '/administration/catalog/bundles/',
        is_legacy_url: true,
      },
      {
        label: 'Catalog management',
        target_url: '/catalog/management/',
        is_legacy_url: false,
      },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    links: [
      {
        label: 'Stock',
        target_url: '/stock/inventory/stock/',
        is_legacy_url: true,
      },
      {
        label: 'Orders',
        target_url: '/stock/inventory/orders/',
        is_legacy_url: true,
      },
      {
        label: 'Controlled medicines',
        target_url: '/stock/inventory/controlled/',
        is_legacy_url: true,
      },
      {
        label: 'Stock locations',
        target_url: '/stock/inventory/locations/',
        is_legacy_url: true,
      },
      {
        label: 'Wholesalers',
        target_url: '/stock/wholesalers/',
        is_legacy_url: false,
      },
    ],
  },
  ...(featureFlags['temporary.new_frontend.data_practice_performance_overview']
    ? [
        {
          id: 'reporting_v2',
          label: 'Reporting',
          target_url: null,
          is_legacy_url: false,
          links: [
            {
              label: 'Summary',
              target_url: '/reports/summary/',
              is_legacy_url: false,
            },
            {
              label: 'Reports',
              target_url: '/reports/',
              is_legacy_url: false,
            },
          ],
        },
      ]
    : [
        {
          id: 'reporting',
          label: 'Reports',
          target_url: '/reports/',
          is_legacy_url: true,
          links: [],
        },
      ]),
  {
    id: 'settings',
    label: 'Settings',
    target_url: '/organization/administration/index/',
    is_legacy_url: true,
    links: [],
  },
  {
    id: 'user_support',
    label: 'Help center',
    target_url: '/support/',
    is_legacy_url: false,
    links: [],
  },
]
